// usePullToRefresh — touch-driven pull-to-refresh gesture for a
// scrollable container.
//
// Why a custom hook (no library): the UX target is small (one screen,
// one container) and we already have a "no new deps" constraint. The
// surface area below is small enough to test directly.
//
// Gesture model
// -------------
// We listen on `pointerdown` / `pointermove` / `pointerup` so the hook
// works for both touch and trackpad-pull. The pull only "engages" when
// the container is already scrolled to the top (scrollTop === 0) AND
// the user is dragging downward — otherwise we let native scrolling
// handle the gesture.
//
// While engaged, `pullDistance` tracks the finger's vertical delta
// (clamped to `threshold * 1.5` so the spinner can't be dragged off
// into the void). When the user releases at or past `threshold`, we
// flip `isRefreshing = true`, await `onRefresh()`, then reset all
// state. A release below threshold snaps back without firing.
//
// Reduced-motion: with `(prefers-reduced-motion: reduce)` matched, we
// still fire `onRefresh()` on threshold crossing, but skip the
// `pullDistance` reporting so the consumer doesn't animate a moving
// spinner. The "is the user trying to refresh?" semantics survive;
// only the in-progress visual is suppressed.

import { useCallback, useEffect, useRef, useState } from 'react';

export interface PullToRefreshOptions {
  /** Invoked when the user releases at or past `threshold`. */
  onRefresh: () => void | Promise<void>;
  /** Minimum pull distance (px) to trigger a refresh. Default 64. */
  threshold?: number;
  /** When false, the gesture is a no-op. Default true. */
  enabled?: boolean;
}

export interface PullToRefreshState {
  /** True while the user is dragging past scroll-top. */
  isPulling: boolean;
  /** Current pull distance in CSS pixels (0 when not pulling). */
  pullDistance: number;
  /** True while `onRefresh()` is in flight. */
  isRefreshing: boolean;
}

const DEFAULT_THRESHOLD = 64;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function usePullToRefresh(
  containerRef: React.RefObject<HTMLElement | null>,
  opts: PullToRefreshOptions,
): PullToRefreshState {
  const { onRefresh, threshold = DEFAULT_THRESHOLD, enabled = true } = opts;

  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Keep the latest callback in a ref so the listener effect doesn't
  // re-bind every render (a fresh closure on every parent render would
  // tear off the pointermove listener mid-gesture).
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  // Per-gesture mutable state. Lives in a ref so handler functions can
  // read/write without forcing component re-renders.
  const gestureRef = useRef<{
    startY: number;
    pointerId: number;
    active: boolean;
    reducedMotion: boolean;
  } | null>(null);

  const reset = useCallback(() => {
    gestureRef.current = null;
    setIsPulling(false);
    setPullDistance(0);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !enabled) return;

    function onPointerDown(e: PointerEvent) {
      // Only engage when the user is starting from the top of the
      // container. If they're mid-scroll, native scrolling wins and we
      // don't claim the gesture.
      if (!el || el.scrollTop > 0) return;
      // Ignore non-primary pointers (e.g. right-click, multi-touch
      // second finger) to keep the gesture predictable.
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      gestureRef.current = {
        startY: e.clientY,
        pointerId: e.pointerId,
        active: false,
        reducedMotion: prefersReducedMotion(),
      };
    }

    function onPointerMove(e: PointerEvent) {
      const g = gestureRef.current;
      if (!g || e.pointerId !== g.pointerId) return;
      const dy = e.clientY - g.startY;
      // Only react to downward drags. If the user pulls up, abandon
      // the gesture so native scrolling takes over again.
      if (dy <= 0) {
        if (g.active) reset();
        return;
      }
      // Re-check scrollTop here too: a user can scroll-down-then-up
      // and we don't want a stale `active` flag to leak in.
      if (el && el.scrollTop > 0) {
        if (g.active) reset();
        return;
      }
      g.active = true;
      if (g.reducedMotion) {
        // Skip the moving-spinner visual but keep tracking so the
        // release handler can compare against `threshold`.
        setIsPulling(true);
        return;
      }
      const clamped = Math.min(dy, threshold * 1.5);
      setIsPulling(true);
      setPullDistance(clamped);
    }

    async function onPointerEnd(e: PointerEvent) {
      const g = gestureRef.current;
      if (!g || e.pointerId !== g.pointerId) return;
      const dy = e.clientY - g.startY;
      const crossed = g.active && dy >= threshold;
      gestureRef.current = null;
      setIsPulling(false);
      setPullDistance(0);
      if (!crossed) return;
      setIsRefreshing(true);
      try {
        await onRefreshRef.current();
      } finally {
        setIsRefreshing(false);
      }
    }

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerEnd);
    el.addEventListener('pointercancel', onPointerEnd);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerEnd);
      el.removeEventListener('pointercancel', onPointerEnd);
    };
  }, [containerRef, enabled, threshold, reset]);

  return { isPulling, pullDistance, isRefreshing };
}
